import React from "react";
import { neutral, Text } from "@flat-ui";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from "@mui/material";
import {
  DevToolItem,
  FeatureToggle,
  TABLE_HEADERS,
} from "@components/dev-features-dialog/features";

/** Styles for the table container */
const TABLE_CONTAINER_SX = {
  border: `1px solid ${neutral[200]}`,
  borderRadius: 2,
  boxShadow: "none",
};

type DevFeaturesTableProps<T extends DevToolItem | FeatureToggle> = {
  /** Title of the table section */
  title: string;

  /** Items to be displayed in the table */
  items: T[];

  /** Component used to render each row */
  rowComponent: React.ComponentType<{ item: T }>;
};

/**
 * Render a table component for the development features or tools.
 */
export function DevFeaturesTable<T extends DevToolItem | FeatureToggle>({
  title,
  items,
  rowComponent,
}: DevFeaturesTableProps<T>): JSX.Element {
  const RowComponent: React.ComponentType<{ item: T }> = rowComponent;

  return (
    <Box>
      <Text variant="heading16" sx={{ display: "flex", mb: 1 }}>
        {title}
      </Text>
      <TableContainer component={Paper} sx={TABLE_CONTAINER_SX}>
        <Table sx={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <TableCell key={header} align="left" sx={{ p: 1 }}>
                  <Text variant="helpText">{header}</Text>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => {
              const key = `dev-feature-row-${index}`;
              return <RowComponent key={key} item={item} />;
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
